import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function EnterPaymentModal({ invoice, onClose, onPaid }) {
  const balanceDue = invoice.balance_due || 0;
  const [amountReceived, setAmountReceived] = useState(String(balanceDue));
  const [paymentRef, setPaymentRef] = useState('');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const amount = parseFloat(amountReceived) || 0;
    if (amount <= 0) { toast.error('Enter a valid amount.'); return; }
    // A broker portal payment is often less than the full balance (they
    // paid one load off a batch of several, a short-pay, etc.) -- this
    // used to always zero the balance and mark Paid regardless of what
    // was actually received. Overpayment isn't accepted here; that's a
    // credit/refund situation outside what this flow tracks.
    if (amount > balanceDue + 0.005) { toast.error(`Amount can't exceed the balance due ($${balanceDue.toFixed(2)}).`); return; }

    setSaving(true);
    const newBalance = Math.max(0, balanceDue - amount);
    const isFullyPaid = newBalance <= 0.005;
    const payments = [...(invoice.payments || []), { date: paidDate, amount, reference: paymentRef || undefined }];
    await client.entities.Invoice.update(invoice.id, {
      status: isFullyPaid ? 'Paid' : 'Partial',
      balance_due: isFullyPaid ? 0 : newBalance,
      paid_date: paidDate,
      payment_reference: paymentRef || undefined,
      payments,
    });
    toast.success(isFullyPaid
      ? `Invoice ${invoice.invoice_number} marked as paid.`
      : `$${amount.toFixed(2)} payment recorded — $${newBalance.toFixed(2)} still due.`);
    setSaving(false);
    onPaid();
    onClose();
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-sm p-6 space-y-4 z-10">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground">Record Payment Received</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground">
          Invoice <span className="text-foreground font-semibold">{invoice.invoice_number}</span> — {invoice.customer_name}<br />
          Balance Due: <span className="text-primary font-bold">${balanceDue.toLocaleString()}</span>
        </p>
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Amount Received ($)</label>
          <input
            type="number"
            value={amountReceived}
            onChange={e => setAmountReceived(e.target.value)}
            step="0.01"
            className={inputCls}
          />
          {parseFloat(amountReceived) > 0 && parseFloat(amountReceived) < balanceDue - 0.005 && (
            <p className="text-[11px] text-amber-500 mt-1">
              Partial payment — ${(balanceDue - parseFloat(amountReceived)).toFixed(2)} will still be due.
            </p>
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Paid Date</label>
          <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Payment Reference (Optional)</label>
          <input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="Check #, ACH ref, Stripe/portal ref, etc." className={inputCls} />
        </div>
        <div className="flex gap-3">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {saving
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              : (parseFloat(amountReceived) < balanceDue - 0.005 ? 'Record Partial Payment' : 'Mark Invoice Paid')}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">Cancel</Button>
        </div>
      </div>
    </div>
  );
}